import cuid from "cuid";
import { filter, get, isNil, last, sortBy } from "lodash";
import { useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { CREATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { OFFERS } from "utils/api/graphql/queries/offers";
import { JOB_LIST } from "utils/constants";
import { filterByConditions } from "../../../..";
import useInsurancePlans from "../../../Manual/Solyon/Comparison/Widgets/Upsells/hooks/useInsurancePlans";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

const Upsells = ({ processId, project, onNext, onBack, tracer }) => {
  const [addSubscription] = useMutation(CREATE_SUBSCRIPTION);

  const [selectedModules, setSelectedModules] = useState(
    get(project, "fields.upsells", []),
  );

  const [getOffers, { loading: offersLoading }] = useLazyQuery(OFFERS);
  const { selectedInsurancePlans, fetchMainOffers, loading } =
    useInsurancePlans({
      insurancePlan: { id: get(project, "fields.lastSelected.0") },
      getOffers,
      project,
      setSelectedModules,
      offerWhere: [
        {
          offerInsurancePlans: {
            weight: get(project, "fields.hasUpsells") ? 1 : 2,
          },
        },
      ],
    });
  useQuery(OFFERS, {
    fetchPolicy: "no-cache",
    variables: {
      where: {
        AND: [
          {
            offerInsurancePlans: {
              insurancePlan: { id: get(project, "fields.lastSelected.0") },
            },
          },
          { offerInsurancePlans: { main: true } },
        ],
      },
    },
    onCompleted: (offers) => {
      fetchMainOffers(offers);
    },
  });

  const handleModule = ({ module }) => {
    const id = get(module, "insurancePlan.id");
    setSelectedModules((prev) => {
      const exist = get(prev, get(project, "fields.lastSelected.0"))?.find(
        ({ insurancePlan }) => insurancePlan.id === id,
      );
      return exist
        ? {
            ...prev,
            [get(project, "fields.lastSelected.0")]: get(
              prev,
              get(project, "fields.lastSelected.0"),
              [],
            ).filter(({ insurancePlan }) => insurancePlan.id !== id),
          }
        : {
            ...prev,
            [get(project, "fields.lastSelected.0")]: [
              ...get(prev, get(project, "fields.lastSelected.0"), []),
              module,
            ],
          };
    });
  };

  const onSubmit = async ({ ignore = false }) => {
    const profession = get(project, "contact.profession");
    if (
      [
        JOB_LIST.HOSPITAL_CIVIL_SERVICE,
        JOB_LIST.HOSPITAL_PRACTITIONER,
      ].includes(profession) &&
      get(project, "fields.hasCds")
    )
      return onNext({
        payload: {
          fields: {
            ...project.fields,
            upsells: ignore ? undefined : selectedModules,
          },
        },
      });
    const insurancePlanId = get(project, "fields.lastSelected.0");
    const insurancePlans = [
      ...get(project, "fields.insurancePlans", []).filter(
        (id) => id !== insurancePlanId,
      ),
      insurancePlanId,
    ];
    const status = get(
      last(
        sortBy(
          filter(
            project.statuses,
            ({ status }) => status.process.id === processId,
          ),
          "createdDate",
        ),
      ),
      "status",
    );
    const { possibleStatuses } = status;
    const [possibleStatus] = filterByConditions(
      sortBy(possibleStatuses, "order"),
      {
        ...project,
        status,
      },
    );

    addSubscription({
      variables: {
        data: {
          id: cuid(),
          project: { id: project.id },
          insurancePlan: {
            id: insurancePlanId,
          },
        },
      },
      onCompleted: ({ addSubscription: { id: subscriptionId } }) => {
        onNext({
          payload: {
            locked: true,
            visible: true,
            fields: {
              ...project.fields,
              insurancePlans: insurancePlans,
              ...(!isNil(possibleStatus?.nextStatus) && {
                nextQuoteStatus: possibleStatus.nextStatus,
              }),
              currentSubscription: subscriptionId,
              upsells: ignore ? undefined : selectedModules,
            },
          },
        });
      },
    });
  };

  if (offersLoading || loading) return <Loader />;

  return (
    <>
      <Navbar
        progress={60}
        project={project}
        tracer={tracer}
        showCart
        showProfile
        step={3}
        stepCount={5}
        title={"Je renforce ma couverture"}
      />
      <View
        contact={get(project, "contact", {})}
        onNext={onSubmit}
        onBack={onBack}
        upsells={selectedInsurancePlans}
        selectedModules={selectedModules}
        projectId={project?.id}
        handleModule={handleModule}
        insurancePlanId={get(project, "fields.lastSelected.0")}
      />
    </>
  );
};

export default Upsells;
